import React, { useEffect, useRef, useState } from 'react';

import { string, func, bool, array } from 'prop-types';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useHistory } from 'react-router-dom';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { propTypes } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { ensureListing, ensureUser, prepareAbbreviatedName } from '../../util/data';

const { Money } = sdkTypes;
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { resolveListingCondition } from '../../util/listingCondition';
import { isBookingProcessAlias } from '../../transactions/transaction';

import {
  AspectRatioWrapper,
  Avatar,
  Button,
  InlineTextButton,
  NamedLink,
  ResponsiveImage,
  ReviewRating,
} from '../../components';
import OptimizedImage from './OptimizedImage';

import css from './ListingCard.module.css';
import {
  ASSOCIATED,
  getBreadcrumb,
  getCategoriesAndSubcategories,
  getCreatorUser,
  listingIsDraft,
} from '../../util/listingsHelpers';
import { createResourceLocatorString } from '../../util/routes';
import { prepareMemberData } from '../../util/userHelpers';

const _ = require('lodash');

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const VideoHoverComponent = ({ videoUrl, image, variants, renderSizes, title }) => {
  const videoRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  // Detect if it's a touch device
  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  // Effect to handle video play on touch devices when video becomes visible
  useEffect(() => {
    if (isHovered && isTouchDevice && videoRef.current && videoLoaded) {
      // Small delay to ensure video is ready
      setTimeout(() => {
        if (videoRef.current && isHovered) {
          videoRef.current.play().catch(err => {
            console.log('Video autoplay failed on touch device:', err);
          });
        }
      }, 200);
    }
  }, [isHovered, isTouchDevice, videoLoaded]);

  const handleMouseEnter = () => {
    if (!isTouchDevice) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isTouchDevice) {
      setIsHovered(false);
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    }
  };

  const handleTouchStart = e => {
    if (isTouchDevice) {
      e.preventDefault(); // Prevent default touch behavior
      setIsHovered(!isHovered); // Toggle video on touch

      // Force play on touch for iOS
      if (!isHovered && videoRef.current) {
        setTimeout(() => {
          if (videoRef.current) {
            videoRef.current.play().catch(err => {
              console.log('Video play failed on touch:', err);
            });
          }
        }, 100);
      }
    }
  };

  const handleVideoLoaded = () => {
    setVideoLoaded(true);
    if (videoRef.current) {
      // For touch devices, don't autoplay - wait for user interaction
      if (!isTouchDevice) {
        videoRef.current.play().catch(err => {
          console.log('Video play failed:', err);
        });
      }
    }
  };

  return (
    <div
      className={css.rootForImage}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      style={{
        position: 'relative',
        cursor: 'pointer',
      }}
    >
      {/* Always show the image */}
      <OptimizedImage
        rootClassName={css.rootForImage}
        alt={title}
        image={image}
        variants={variants}
        sizes={renderSizes}
      />

      {/* Video overlay - loads on hover/touch */}
      {isHovered && (
        <video
          ref={videoRef}
          className={css.rootForImage}
          loop
          muted
          playsInline
          webkit-playsinline="true"
          preload="metadata"
          onLoadedData={handleVideoLoaded}
          onCanPlay={handleVideoLoaded}
          onTouchStart={e => {
            if (isTouchDevice && videoRef.current) {
              e.stopPropagation();
              videoRef.current.play().catch(err => {
                console.log('Video touch play failed:', err);
              });
            }
          }}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: videoLoaded ? 0.9 : 0,
            transition: 'opacity 0.3s ease-out',
            zIndex: 1,
          }}
        >
          <source src={videoUrl.url} type="video/mp4" />
        </video>
      )}
    </div>
  );
};

// Simple customer commission calculation for price display
const calculateSimpleCustomerCommission = (basePrice, customerCommissionPercentage) => {
  const commissionAmount = Math.round((basePrice.amount * customerCommissionPercentage) / 100);
  return new Money(basePrice.amount + commissionAmount, basePrice.currency);
};

// Utility function to calculate commission-adjusted price for customers
const calculateCustomerPriceWithCommission = (basePrice, config, commissionData) => {
  if (!basePrice) {
    return basePrice;
  }

  // Use commission data from Sharetribe Console if available, otherwise fallback to config
  let customerCommissionPercentage;

  if (commissionData?.customerCommission?.percentage) {
    customerCommissionPercentage = commissionData.customerCommission.percentage;
  } else if (config?.commission?.showCustomerCommission) {
    customerCommissionPercentage = config.commission.defaultCustomerCommissionPercentage;
  } else {
    // No commission configuration found, show base price
    return basePrice;
  }

  // Apply simple customer commission (just percentage of base price)
  return calculateSimpleCustomerCommission(basePrice, customerCommissionPercentage);
};

const priceData = (price, currency, intl, config = null, commissionData = null) => {
  if (price && price.currency === currency) {
    // Calculate commission-adjusted price for customers
    const adjustedPrice = calculateCustomerPriceWithCommission(price, config, commissionData);
    const formattedPrice = formatMoney(intl, adjustedPrice);

    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl, commissionData = null } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(
    price,
    config.currency,
    intl,
    config,
    commissionData
  );
  return (
    <div className={css.price}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      {isBookable ? (
        <div className={css.perUnit}>
          <FormattedMessage
            id="SearchListingCard.perUnit"
            values={{ unitType: publicData?.unitType }}
          />
        </div>
      ) : null}
    </div>
  );
};

export const SearchListingCardComponent = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const history = useHistory();
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo,
    isIframe,
    showEdit,
    showDelete,
    onDelete,
    showActions,
    commissionData,
    commissionLoading,
    commissionError,
  } = props;

  const videoRef = useRef(null);

  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, description, publicData = {} } = currentListing.attributes || {};
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const categoryBreadcrumb = getBreadcrumb(config, listing);
  const { marketplaceName } = config;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'landscape-crop2x',
  } = config.layout.listingImage;

  // Use custom variant prefix if provided (for manufacturer listings)
  const finalVariantPrefix = props.customVariantPrefix || variantPrefix;

  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(finalVariantPrefix))
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const { brand, isAssociated = false, creatorType, videos = [] } = publicData;
  const conditionLabel = resolveListingCondition(publicData);
  // const ratingData = [4, 3]; // Todo: Get data from source
  // const rating = ratingData.length ? ratingData.reduce((a, b) => a + b, 0) / ratingData.length : 0;
  // const ratingFloor = Math.floor(rating);
  // const ratingShow = (Math.round(rating * 10) / 10).toFixed(1).replace('.0', '');

  // const imageVariants = firstImage?.attributes?.variants || [];
  // const image = imageVariants['landscape-crop2x'] || imageVariants['landscape-crop'] || imageVariants['default'] || imageVariants['square-small2x'] || imageVariants['square-small'] || imageVariants['landscape-crop2x'] || imageVariants['landscape-crop'];

  const MAX_TITLE_LENGTH = 20;

  let listingProps = {
    name: 'ListingPage',
    params: { id, slug },
    ...(isIframe ? { to: { search: 'iframe' } } : { target: '_self' }),
  };

  let editListingProps = {
    name: 'EditListingPage',
    params: { id, slug, type: 'edit', tab: 'details' },
  };

  let isDraft = listingIsDraft(currentListing);

  let reviews = [];

  let store = isAssociated ? props.stores.find(s => s.id.uuid == author.id.uuid) : null;
  let businessName;

  if (store) {
    businessName = store?.attributes?.profile?.publicData?.businessName;
    let businessOwnerName = store?.attributes?.profile?.publicData?.businessOwnerName;
    let displayName = store?.attributes?.profile?.displayName;
    store.attributes.profile.abbreviatedName = prepareAbbreviatedName(
      businessName || businessOwnerName || displayName || ''
    );

    reviews = store?.attributes?.reviews || [];
  } else if (isAssociated) {
    // If isAssociated is true, but store is not found, use the author's business name
    businessName = author?.attributes?.profile?.publicData?.businessName;
  }

  const authorUser = _.cloneDeep(author);
  const creatorUser = getCreatorUser(listing, props.creators);

  let {
    authorObj,
    authorId,
    authorName,
    rating,
    ratingCounter,
    authorType,
    reviews: memberReviews,
  } = prepareMemberData(authorUser, creatorUser, creatorType, reviews, isAssociated);
  reviews = memberReviews.length ? memberReviews : reviews;

  let canShowTeam = props.showTeam && authorType !== 'own';

  const inlineListener = (event, name, params) => {
    event.preventDefault();
    event.stopPropagation();
    let uri = createResourceLocatorString(name, routeConfiguration, params, {});
    if (!isIframe && typeof window !== 'undefined') {
      window.open(uri, '_blank');
    } else {
      history.push(uri);
    }
  };

  let profilePage = 'ProfilePage';
  let userRouteParams = {
    id: authorId || author.id.uuid,
  };

  if (authorType !== 'own') {
    let isMemberAssociated = authorType == ASSOCIATED;
    profilePage = isMemberAssociated ? 'TeamPageMember' : 'TeamPage';
    userRouteParams = {
      id: store?.id?.uuid,
      type: isMemberAssociated ? 'member' : 'owner',
      ...(isMemberAssociated ? { memberId: authorId || author.id.uuid } : {}),
    };
  }

  let teamData = classStr =>
    showAuthorInfo ? (
      <div className={classStr}>
        <div className={css.authorName}>
          Sold by
          <InlineTextButton
            onClick={event =>
              !isIframe ? inlineListener(event, profilePage, userRouteParams) : null
            }
          >
            {authorName}
          </InlineTextButton>
          {authorType !== 'own'
            ? `, Sales ${authorType == ASSOCIATED ? 'Associate' : 'Owner'}`
            : null}
          {canShowTeam ? ' at ' : null}
          {canShowTeam ? (
            <InlineTextButton
              onClick={event =>
                inlineListener(event, 'TeamPage', { id: store.id.uuid, type: 'detail' })
              }
            >
              {businessName || marketplaceName}
            </InlineTextButton>
          ) : null}
        </div>
      </div>
    ) : null;

  const hasVideo = videos && Array.isArray(videos) && videos.length;
  const videoId = hasVideo ? videos[0] : null;

  // Prepare video URL for hover component (but don't load the video yet)
  const videoUrl =
    hasVideo && videoId
      ? {
          url: `${process.env.REACT_APP_CLOUDFRONT_URL}/${videoId}`,
        }
      : null;

  return (
    <div>
      <NamedLink
        className={classNames(classes, css.searchListingCardContainer, { [css.isDraft]: isDraft })}
        {...listingProps}
      >
        {isDraft ? (
          <div className={css.draftOverlay}>
            <div>
              <FormattedMessage
                id="ManageListingCard.draftOverlayText"
                values={{ listingTitle: title }}
              />
            </div>
            <div>
              {showEdit ? (
                <NamedLink {...editListingProps}>
                  <Button>{intl.formatMessage({ id: 'ListingPage.finishListing' })}</Button>
                </NamedLink>
              ) : null}
            </div>
          </div>
        ) : null}
        <AspectRatioWrapper
          className={css.aspectRatioWrapper}
          width={aspectWidth}
          height={aspectHeight}
          {...setActivePropsMaybe}
        >
          <div className={css.imageOverlayContainer}>
            {conditionLabel ? <div className={css.conditionBadge}>{conditionLabel}</div> : null}
            {hasVideo && videoUrl ? (
              <VideoHoverComponent
                videoUrl={videoUrl}
                image={firstImage}
                variants={variants}
                renderSizes={renderSizes}
                title={title}
              />
            ) : (
              <OptimizedImage
                rootClassName={css.rootForImage}
                alt={title}
                image={firstImage}
                variants={variants}
                sizes={renderSizes}
              />
            )}
          </div>
        </AspectRatioWrapper>
        <div className={css.mainSectionInfo}>
          <div className={css.listingMainInfo}>
            <div className={css.categoryTitle}>
              <div>
                {richText(title, {
                  longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                  longWordClass: css.longWord,
                })}
                {/* {title.substring(0, MAX_TITLE_LENGTH)} */}
              </div>
              <div className={css.brand}>{brand || '\u00A0'}</div>
            </div>
          </div>
          {categoryBreadcrumb ? (
            <div className={css.categoryLabel}>{categoryBreadcrumb}</div>
          ) : null}
          {teamData(css.authorAndReviewContainerMobile)}
          {showAuthorInfo ? (
            <div className={css.listingCardAuthorContainer}>
              <div className={css.authorPicture}>
                <Avatar className={css.avatar} user={authorObj} disableProfileLink={true} />
              </div>
              {teamData(css.authorAndReviewContainer)}
            </div>
          ) : null}
          <PriceMaybe
            price={price}
            publicData={publicData}
            config={config}
            intl={intl}
            commissionData={commissionData}
          />
        </div>
      </NamedLink>
      {showActions ? (
        <div
          className={classNames(css.categoryListingButtonContainer, {
            [css.categoryWithDelete]: showDelete,
          })}
        >
          {showEdit ? (
            <NamedLink {...editListingProps}>
              <Button>Edit</Button>
            </NamedLink>
          ) : null}
          {showDelete ? <Button onClick={onDelete}>Delete</Button> : null}
        </div>
      ) : null}
    </div>
  );
};

SearchListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
  isIframe: false,
  showEdit: false,
  showDraft: false,
  showDelete: false,
  showActions: false,
  showTeam: true,
  stores: [],
  creators: [],
};

SearchListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  showAuthorInfo: bool,
  isIframe: bool,
  showEdit: bool,
  showDraft: bool,
  showDelete: bool,
  showActions: bool,
  showTeam: bool,
  stores: array,
  creators: array,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
};

export default injectIntl(SearchListingCardComponent);
